"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";
import { ApiError, startAnalyze, startImageAudit } from "../../../lib/api";
import { validateAuditUrl } from "../../../lib/guest-audit";
import {
  isWarnPreview,
  SiteFavicon,
  SitePreviewMessage,
  useSitePreflight,
} from "../../../components/site-preview";
import { isOurOwnDomain, SELF_AUDIT_MESSAGE } from "../../../lib/self-domain";
import type { RunMode, Viewport } from "../../../lib/audit-types";
import { readClientId } from "../../../lib/auth";
import { useAppAuth } from "../../app-shell";
import { AppToMarketingLink } from "../../components/app-to-marketing-link";
import {
  SettingLabel,
  OutputGlyph,
  DeviceGlyph,
  PagesGlyph,
  openUpgradeInNewTab,
  chipLabelFor,
  loadAuditDefaults,
} from "../../components/setting-label";

/* ------------------------------------------------------------------ */
/*  Types & constants                                                   */
/* ------------------------------------------------------------------ */

type ViewportOption = "desktop" | "mobile" | "both";
type InputSource = "url" | "image";

const PAGE_OPTIONS = [1, 3, 5, 10] as const;
const RECENT_URLS_KEY = "snap-site:web-recent-urls";
const MAX_RECENT_URLS = 6;
const AI_EXAMPLES = ["Capture pricing & checkout", "Open the mobile menu", "Switch pricing to annual"];

// Mirrors backend /jobs/image validation. Update both if either changes.
const IMAGE_ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const IMAGE_ACCEPT_ATTR = IMAGE_ACCEPTED_TYPES.join(",");
const IMAGE_MAX_BYTES = 4_500_000;

type AuditPreset = {
  key: string;
  label: string;
  mode: RunMode;
  viewport: ViewportOption;
  pages: number;
  aiNote?: string;
  requires?: "responsive" | "ai";
};

/**
 * Presets are shortcuts that meaningfully diverge from the form's
 * defaults. The "Full site audit" preset was removed because its
 * settings (audit / desktop / 3 pages) match the defaults exactly —
 * clicking it did nothing, which made the preset row feel broken.
 * The remaining three each set the form to a configuration the user
 * would otherwise have to assemble by clicking through several rows:
 * drop to a single page, add a second viewport, or switch the output
 * to screenshots-only.
 *
 * Labels are warmed up to read like what a user would say to
 * themselves — "Just this page" instead of "Homepage only" (the
 * preset doesn't actually require a homepage URL), "Desktop + mobile"
 * instead of "Responsive pass" (the audience for this feature is
 * folks who want both screen sizes, not designers shopping for
 * jargon). The third preset switches the output away from an AI
 * audit and toward bare screenshots; it's labeled in terms of what
 * the user gets ("Screenshots only") rather than what the system
 * does ("snapshot mode"), and matches the existing in-product
 * vocabulary used in the mode toggle's helper text.
 */
const AUDIT_PRESETS: AuditPreset[] = [
  {
    key: "single",
    label: "Just this page",
    mode: "audit",
    viewport: "desktop",
    pages: 1,
  },
  {
    key: "responsive",
    label: "Desktop + mobile",
    mode: "audit",
    viewport: "both",
    pages: 5,
    requires: "responsive",
  },
  {
    key: "screens",
    label: "Screenshots only",
    mode: "snapshot",
    viewport: "desktop",
    pages: 5,
  },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function hostnameFor(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

function pushRecentUrl(value: string) {
  try {
    const raw = window.localStorage.getItem(RECENT_URLS_KEY);
    const prev: string[] = raw ? JSON.parse(raw) : [];
    const next = [value, ...prev.filter((r) => r !== value)].slice(0, MAX_RECENT_URLS);
    window.localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

function viewportsForOption(v: ViewportOption): Viewport[] {
  return v === "both" ? ["desktop", "mobile"] : [v];
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                            */
/* ------------------------------------------------------------------ */

function AuditConfigSkeleton() {
  return (
    <div className="nr-page">
      <div className="nr-header">
        <span className="sk sk-title" style={{ width: 160 }} />
      </div>
      <div className="rc nr-form" style={{ minHeight: 320 }}>
        <span className="sk" style={{ width: "100%", height: 52, borderRadius: 14, marginBottom: 16 }} />
        <span className="sk sk-line" style={{ width: "100%", height: 72, borderRadius: 12 }} />
        <span className="sk sk-line" style={{ width: "60%", height: 36, borderRadius: 10, marginTop: 20 }} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inner form (needs useSearchParams inside Suspense)                  */
/* ------------------------------------------------------------------ */

function AuditConfigInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const auth = useAppAuth();

  /* Pre-fill URL from query string (?url=...) */
  const initialUrl = searchParams.get("url") ?? "";
  /* Pre-select mode from query string (?mode=imageAudit, etc.) */
  const initialMode: RunMode = (() => {
    const m = searchParams.get("mode");
    return m === "imageAudit" || m === "snapshot" || m === "audit" ? m : "audit";
  })();

  const [url, setUrl] = useState(initialUrl);
  const [mode, setMode] = useState<RunMode>(initialMode);
  const [viewport, setViewport] = useState<ViewportOption>("desktop");
  const [pages, setPages] = useState(3);
  const [aiNote, setAiNote] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recents, setRecents] = useState<string[]>([]);

  // "Site card" evidence line under the URL bar — shared with the marketing
  // hero form (components/site-preview.tsx): the page's real title, an
  // empty-shell warning (airdnb.com), a cross-host redirect, or "did you
  // mean airbnb.com?". Warn-level verdicts arm a one-click "are you sure?"
  // on submit; nothing is ever hard-blocked, and paid users keep full
  // control ("Audit anyway" proceeds).
  const [confirmArmed, setConfirmArmed] = useState(false);
  // When the confirm was armed — clicks inside the cooldown are swallowed so
  // a double-click can't self-confirm a question that hadn't painted yet.
  const confirmArmedAt = useRef(0);
  // Submit arrived before the typing preview resolved: hold the click and
  // resolve the SAME preflight (shared request); CTA reads "Checking…".
  const [checkingUrl, setCheckingUrl] = useState(false);
  const disarmConfirm = useCallback(() => setConfirmArmed(false), []);
  const { preview: sitePreview, ensure: ensureSitePreview } = useSitePreflight(
    mode === "imageAudit" ? "" : url,
    "config",
    disarmConfirm
  );

  // Upload state — only used when mode === "imageAudit".
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-create the preview URL whenever the file changes; revoke on cleanup
  // so we don't leak blob URLs.
  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  /* Sync URL state if query param changes */
  useEffect(() => {
    const qUrl = searchParams.get("url");
    if (qUrl) setUrl(qUrl);
  }, [searchParams]);

  /* Sync mode if query param changes (e.g. deep-link to ?mode=imageAudit) */
  useEffect(() => {
    const qMode = searchParams.get("mode");
    if (qMode === "imageAudit" || qMode === "snapshot" || qMode === "audit") {
      setMode(qMode);
    }
  }, [searchParams]);

  /* Load recent URLs from localStorage */
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECENT_URLS_KEY);
      if (raw) setRecents(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
  }, []);

  /* Apply the user's saved audit defaults from the Settings page.
     Without this, anything they set in /settings was a write-only
     sink — saved but never read. Query-string params (?mode=...,
     ?url=...) still take priority because they're explicit user
     intent for this specific visit. Feature gates further narrow
     the applied values: a saved "mobile" device won't apply for a
     Free user even if they once toggled it on a paid plan and then
     downgraded. */
  useEffect(() => {
    const defaults = loadAuditDefaults();
    if (!defaults) return;
    setPages(defaults.defaultPages);
    setAiOpen(defaults.aiGuidance);
    // Viewport application waits for the feature-gates effect below
    // to clamp invalid values — set it here and trust that effect to
    // downgrade if the user no longer has access.
    setViewport(defaults.devicePreference);
    // Run once on mount only — after that the user is driving the
    // form and we shouldn't reset their in-flight choices.
  }, []);

  /* Feature gates */
  const features = auth.status === "authenticated" ? auth.entitlements.features : null;
  const canMobile = features?.mobile ?? false;
  const canBoth = features?.bothViewports ?? false;
  const canAi = features?.aiInteractions ?? false;
  const maxPages = features?.maxPagesPerRun ?? 5;
  const isImageMode = mode === "imageAudit";
  // Derived input source — single source of truth derived from `mode`.
  // Source = "image" iff mode === "imageAudit"; "url" otherwise.
  // Used by the source tabs at the top of the form, paralleling the
  // pattern in the dashboard's embedded creator.
  const source: InputSource = isImageMode ? "image" : "url";

  useEffect(() => {
    if (viewport === "mobile" && !canMobile) setViewport("desktop");
    if (viewport === "both" && !canBoth) setViewport("desktop");
  }, [viewport, canMobile, canBoth]);

  /* Credit estimate */
  const creditCost = useMemo(() => {
    if (mode === "imageAudit") return 1;
    const vpCount = viewport === "both" ? 2 : 1;
    const effectivePages = Math.min(pages, maxPages);
    const base = vpCount * effectivePages;
    // AI assistance applies to screenshots too (it directs which
    // pages/states to capture); imageAudit already returned above.
    const aiBonus = aiNote.trim() && canAi ? 5 : 0;
    return base + aiBonus;
  }, [mode, viewport, pages, maxPages, aiNote, canAi]);

  if (auth.status !== "authenticated") return <AuditConfigSkeleton />;

  const { token } = auth;
  const credits = auth.entitlements.credits.balance;
  const enoughCredits = credits >= creditCost;

  /* Source helpers — switching source updates `mode` since it encodes
     both source and output. Image source forces mode=imageAudit; URL
     source defaults back to "audit" (snapshot is opt-in via the
     Output toggle below). */
  function selectSource(next: InputSource) {
    setError("");
    if (next === "image") {
      setMode("imageAudit");
    } else if (mode === "imageAudit") {
      setMode("audit");
    }
  }

  /* Preset helpers */
  function canUsePreset(p: AuditPreset) {
    if (p.requires === "responsive") return canBoth;
    if (p.requires === "ai") return canAi;
    return true;
  }

  function isPresetActive(p: AuditPreset) {
    return mode === p.mode && viewport === p.viewport && pages === p.pages;
  }

  function applyPreset(p: AuditPreset) {
    if (!canUsePreset(p)) {
      openUpgradeInNewTab();
      return;
    }
    setMode(p.mode);
    setViewport(p.viewport);
    setPages(p.pages);
    setAiNote(p.aiNote ?? "");
    setAiOpen(Boolean(p.aiNote));
    setError("");
  }

  /* Image-upload helpers */
  function validateImageFile(file: File): string | null {
    if (!(IMAGE_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
      return "Only PNG, JPEG, or WebP files are supported.";
    }
    if (file.size === 0) return "That file looks empty.";
    if (file.size > IMAGE_MAX_BYTES) {
      return `Image is too large. Max ${(IMAGE_MAX_BYTES / 1_000_000).toFixed(1)} MB.`;
    }
    return null;
  }

  function pickImage(file: File | null | undefined) {
    setError("");
    if (!file) {
      setImageFile(null);
      return;
    }
    const validation = validateImageFile(file);
    if (validation) {
      setError(validation);
      setImageFile(null);
      return;
    }
    setImageFile(file);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickImage(file);
  }

  /* Submit */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || checkingUrl) return;

    if (isImageMode) {
      if (!imageFile) {
        setError("Choose a screenshot to upload.");
        return;
      }
      if (!enoughCredits) {
        setError(`This upload needs ${creditCost} credit${creditCost === 1 ? "" : "s"} but you have ${credits}.`);
        return;
      }
      setSubmitting(true);
      setError("");
      try {
        const result = await startImageAudit(token, {
          file: imageFile,
          clientId: readClientId(),
        });
        router.replace(`/audit/${result.jobId}`);
      } catch (err) {
        // Classify on HTTP status, not on substring-matching the message.
        // The backend returns precise, user-facing copy with an accurate
        // status (402 billing/plan, 413 too-large) — surface it verbatim
        // instead of re-bucketing by keyword, which mislabeled unrelated
        // errors as "out of credits".
        const status = err instanceof ApiError ? err.status : undefined;
        const msg = err instanceof Error ? err.message : "";
        if (status === 402) {
          setError(msg || "This upload needs more credits than you have. Add credits or upgrade your plan.");
          // Billing rejection => our cached balance may be stale. Reconcile
          // the badge so it stops contradicting the error.
          void auth.refresh();
        } else if (status === 413) {
          setError(msg || `Image is too large. Max ${(IMAGE_MAX_BYTES / 1_000_000).toFixed(1)} MB.`);
        } else if (status && status >= 400 && status < 500) {
          setError(msg || "Could not start the image audit. Try a different file.");
        } else {
          setError("Could not start the image audit. Your credits were not used — try again.");
        }
        setSubmitting(false);
      }
      return;
    }

    const trimmed = url.trim();
    if (!trimmed) {
      setError("Enter a URL to audit.");
      return;
    }
    // Self-audit guard — block our own site before credits or any API call.
    if (isOurOwnDomain(trimmed)) {
      setError(SELF_AUDIT_MESSAGE);
      return;
    }
    // Structural URL sanity before credits or any API call — same rules the
    // backend enforces pre-charge (see backend/src/url-validation.ts), run
    // locally so a typo ("easy mails.cc") answers instantly.
    const validUrl = validateAuditUrl(trimmed);
    if (!validUrl.ok) {
      setError(
        validUrl.suggestion
          ? `${validUrl.message} Did you mean ${validUrl.suggestion}?`
          : validUrl.message
      );
      return;
    }
    // Double-click guard: an armed confirm ignores clicks for a beat, so
    // "click-click" can't blow through a question that hadn't even painted.
    if (confirmArmed && Date.now() - confirmArmedAt.current < 600) return;

    // "Are you sure?" — one interception when the verdict for THE HOST BEING
    // SUBMITTED is warn-level. If the typing preview hasn't resolved yet
    // (fast paste-and-click), hold the click and resolve it now. Still
    // fail-open: a timed-out or failed check proceeds like today.
    if (!confirmArmed) {
      let verdict =
        sitePreview.status === "ready" &&
        sitePreview.hostname === validUrl.hostname
          ? sitePreview
          : null;
      if (!verdict) {
        setCheckingUrl(true);
        const resolved = await ensureSitePreview(trimmed);
        setCheckingUrl(false);
        if (
          resolved.status === "ready" &&
          resolved.hostname === validUrl.hostname
        ) {
          verdict = resolved;
        }
      }
      if (verdict && isWarnPreview(verdict)) {
        setConfirmArmed(true);
        confirmArmedAt.current = Date.now();
        return;
      }
    }
    if (!enoughCredits) {
      setError(`This run needs ${creditCost} credit${creditCost === 1 ? "" : "s"} but you have ${credits}.`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const effectivePages = Math.min(pages, maxPages);
      const result = await startAnalyze(token, {
        url: trimmed,
        runMode: mode,
        viewports: viewportsForOption(viewport),
        heuristicsAudit: mode === "audit",
        notes: mode === "audit",
        highlights: false,
        maxPages: effectivePages,
        clientId: readClientId(),
        ...(source === "url" && aiNote.trim() && canAi ? { aiNote: aiNote.trim() } : {}),
      });
      pushRecentUrl(trimmed);
      router.replace(`/audit/${result.jobId}`);
    } catch (err) {
      // Classify on HTTP status, not on substring-matching the message.
      // The backend already returns accurate, user-facing copy per reason
      // with a real status: 402 = billing/plan gate ("Not enough credits…",
      // "Your plan supports up to N pages per run", "Mobile … on Pro and
      // Agency"), 413 = run limits. Surfacing that text verbatim is strictly
      // more accurate than the old keyword bucketing, which lumped plan /
      // page-limit / provider errors into a single "out of credits" message
      // that contradicted the visible balance.
      const status = err instanceof ApiError ? err.status : undefined;
      const msg = err instanceof Error ? err.message : "";
      if (status === 402 || status === 413) {
        setError(
          msg ||
            "This audit can’t run as configured. Capture fewer pages, add credits, or upgrade your plan.",
        );
        // A billing rejection (402) means our cached balance may be stale —
        // reconcile the badge so it stops contradicting the error.
        if (status === 402) void auth.refresh();
      } else if (status && status >= 400 && status < 500) {
        setError(msg || "We couldn’t start this audit. Check the URL or try a public link.");
      } else {
        // Don't promise "your credits weren't charged" — we can't always
        // know. If the job creation succeeded backend-side but the
        // response failed mid-flight, the charge may have already
        // happened. Promising otherwise erodes trust the first time
        // it's wrong. Tell the user the action didn't complete and
        // give them a clear recovery path (check Files, contact us).
        setError(
          "Something went wrong. Check Files in a moment to see if the audit started; if not, try again or contact support if you see an unexpected charge.",
        );
      }
      setSubmitting(false);
    }
  }

  /* CTA: cost suffix pairs with the verb. During submit, copy names
     what's starting so the user knows the job kicked off. */
  const costSuffix = ` · ${creditCost} credit${creditCost === 1 ? "" : "s"}`;
  const ctaActionLabel = isImageMode
    ? "Start audit"
    : mode === "snapshot"
      ? "Take screenshots"
      : "Start audit";
  const submittingLabel = isImageMode
    ? "Starting upload…"
    : mode === "snapshot"
      ? "Capturing…"
      : "Starting audit…";
  const ctaLabel = submitting
    ? submittingLabel
    : checkingUrl
      ? "Checking the address…"
      : confirmArmed
        ? `Audit anyway${costSuffix}`
        : ctaActionLabel + costSuffix;

  return (
    <div className="nr-page">
      {/* Page header — title + warm subline + credits badge. The
          subline replaces the previous bare-h1 framing; "Configure
          and start in seconds" gives the page a voice. Stays
          full-width above the 2-col grid, same as every other
          tool page (Dashboard / Files / Account / Settings). */}
      <div className="nr-header">
        <div className="nr-header-text">
          <h1 className="nr-title">New audit</h1>
          <p className="nr-subline">Configure and start in seconds.</p>
        </div>
        <span className="nr-credits-badge" aria-label={`${credits} credits available`}>
          {credits} credit{credits !== 1 ? "s" : ""} available
        </span>
      </div>

      {/* 2-col body to match the rest of the tool. Form lives in the
          main column (70%); aside is intentionally empty here — the
          form is the entire job of this page and a contextual side
          card would just add noise. The empty column keeps the
          column width consistent with the other tool pages so the
          eye learns one reading width across the product. */}
      <div className="app-cols">
        <div className="app-main">
      {/* Main form card. Single outer <form> so Enter on URL still
          submits via the bottom CTA — previously the URL bar and
          dropzone were separate <form>s with the submit button
          baked in, which split cost messaging from the verb. */}
      <form className="rc nr-form" onSubmit={handleSubmit}>
        {/* Presets — now inside the card, framed as suggestions
            ("Start from"), and only shown for URL source since they
            all configure URL-audit shapes. Locked presets are clickable
            upgrade nudges, not silent dead pills. */}
        {!isImageMode && (
          <div className="nr-presets-inline">
            <span className="nr-presets-prefix">Start from:</span>
            <div className="nr-presets" role="radiogroup" aria-label="Audit preset">
              {AUDIT_PRESETS.map((p) => {
                const disabled = !canUsePreset(p);
                const active = isPresetActive(p);
                const title = disabled
                  ? `${p.label} is on the Pro plan. Click to see pricing.`
                  : undefined;
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-disabled={disabled}
                    className={`nr-preset${active ? " active" : ""}${disabled ? " disabled" : ""}`}
                    title={title}
                    onClick={() => applyPreset(p)}
                  >
                    {p.label}
                    {p.requires === "responsive" && !canBoth && <span className="rc-pro">Pro</span>}
                    {p.requires === "ai" && !canAi && <span className="rc-pro">Pro</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Input source tabs — URL vs Upload image. Leads the form
            because it dictates which input affordance shows below
            (URL bar vs dropzone). Previously the Type chips lived
            BELOW the URL bar, so the user could pick a URL, then
            realize they wanted Upload, and the form would rewrite
            underneath them. */}
        <div className="rc-tabs" role="tablist" aria-label="Audit input source">
          <button
            type="button"
            role="tab"
            aria-selected={source === "url"}
            className={`rc-tab${source === "url" ? " active" : ""}`}
            onClick={() => selectSource("url")}
          >
            From URL
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={source === "image"}
            className={`rc-tab${source === "image" ? " active" : ""}`}
            onClick={() => selectSource("image")}
          >
            Upload image
          </button>
        </div>

        {/* Input — URL bar or dropzone */}
        {isImageMode ? (
          <div
            className={`nr-dropzone${dragging ? " dragging" : ""}${imageFile ? " has-file" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onClick={() => {
              if (!imageFile) fileInputRef.current?.click();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT_ATTR}
              className="nr-dropzone-input"
              aria-label="Choose an image to upload"
              onChange={(e) => pickImage(e.target.files?.[0])}
            />
            {imageFile && imagePreview ? (
              <div className="nr-dropzone-preview">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imagePreview}
                  alt="Uploaded image preview"
                  className="nr-dropzone-thumb"
                />
                <div className="nr-dropzone-fileinfo">
                  <p className="nr-dropzone-filename">{imageFile.name}</p>
                  <p className="nr-dropzone-filemeta">
                    {(imageFile.size / 1_000_000).toFixed(2)} MB · {imageFile.type.split("/")[1].toUpperCase()}
                  </p>
                  <div className="nr-dropzone-actions">
                    <button
                      type="button"
                      className="nr-dropzone-replace"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="nr-dropzone-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="nr-dropzone-empty">
                <UploadCloudIcon />
                <p className="nr-dropzone-title">Drop an image here, or click to choose</p>
                <p className="nr-dropzone-hint">
                  PNG, JPEG, or WebP. Up to {(IMAGE_MAX_BYTES / 1_000_000).toFixed(1)} MB.
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="rc-bar">
              <LinkIcon />
              <input
                className="rc-bar-input"
                type="text"
                inputMode="url"
                aria-label="Website URL"
                placeholder="Paste a URL to audit"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (error && e.target.value.trim()) setError("");
                  if (confirmArmed) setConfirmArmed(false);
                }}
                autoComplete="off"
                spellCheck={false}
                autoFocus
                disabled={submitting}
              />
            </div>

            {/* Site-card evidence line — shared component + styles with the
                marketing hero form so the two cards never drift apart. */}
            {sitePreview.status === "ready" && (
              <p
                className={`lp-hero-form-preview${
                  isWarnPreview(sitePreview) ||
                  sitePreview.verdict === "noexist"
                    ? " is-warn"
                    : ""
                }`}
                aria-live="polite"
              >
                <SiteFavicon hostname={sitePreview.hostname} />
                <span>
                  <SitePreviewMessage
                    preview={sitePreview}
                    confirmArmed={confirmArmed}
                    onSwap={(host) => {
                      setUrl(host);
                      setConfirmArmed(false);
                    }}
                  />
                </span>
              </p>
            )}

            {/* Recent URLs. Chip labels include the path so two audits
                of the same host don't collapse into identical chips.
                The standalone "Clear" button was dropped — users
                rarely need to wipe recents and the affordance was
                clutter. */}
            {recents.length > 0 && (
              <div className="rc-recents">
                <span className="rc-recents-label">Recent:</span>
                {recents.slice(0, 4).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="rc-recent-chip"
                    onClick={() => setUrl(r)}
                    title={r}
                  >
                    {chipLabelFor(r)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Settings — only for URL source. Image audits have a fixed
            shape (1 page, 1 viewport, 1 credit) so the settings rows
            don't apply. */}
        {!isImageMode && (
          <div className="rc-inline-settings">
            {/* Output — fundamental: what the run produces. Replaces
                the "Type" chip row which previously bundled output
                with input source. */}
            <div className="rc-settings-row">
              <SettingLabel icon={<OutputGlyph />}>What to produce</SettingLabel>
              <div className="rc-toggle">
                <button
                  type="button"
                  className={`rc-toggle-btn${mode === "audit" ? " active" : ""}`}
                  aria-pressed={mode === "audit"}
                  onClick={() => setMode("audit")}
                >
                  Full audit
                </button>
                <button
                  type="button"
                  className={`rc-toggle-btn${mode === "snapshot" ? " active" : ""}`}
                  aria-pressed={mode === "snapshot"}
                  onClick={() => setMode("snapshot")}
                >
                  Screenshots only
                </button>
              </div>
            </div>

            {/* Device — own stacked row so its label aligns to the same
                200px column as the rows above, matching the dashboard
                creator. (Previously combined side-by-side with Pages,
                which broke the column alignment.) */}
            <div className="rc-settings-row">
              <SettingLabel icon={<DeviceGlyph />}>Which device</SettingLabel>
              <div className="rc-toggle rc-toggle-device">
                <button type="button" className={`rc-toggle-btn${viewport === "desktop" ? " active" : ""}`} aria-pressed={viewport === "desktop"} onClick={() => setViewport("desktop")}>
                  <DesktopIcon /> Desktop
                </button>
                {/* Locked chips are clickable upgrade nudges — open
                    pricing in a new tab so the in-progress config
                    isn't lost. Tooltip on hover explains why locked. */}
                <button
                  type="button"
                  className={`rc-toggle-btn${viewport === "mobile" ? " active" : ""}${!canMobile ? " rc-locked" : ""}`}
                  aria-pressed={viewport === "mobile"}
                  aria-disabled={!canMobile}
                  title={canMobile ? undefined : "Mobile audits are on the Pro plan. Click to see pricing."}
                  onClick={() => canMobile ? setViewport("mobile") : openUpgradeInNewTab()}
                >
                  <MobileIcon /> Mobile {!canMobile && <span className="rc-pro">Pro</span>}
                </button>
                <button
                  type="button"
                  className={`rc-toggle-btn${viewport === "both" ? " active" : ""}${!canBoth ? " rc-locked" : ""}`}
                  aria-pressed={viewport === "both"}
                  aria-disabled={!canBoth}
                  title={canBoth ? undefined : "Auditing Desktop + Mobile in one run is on the Pro plan. Click to see pricing."}
                  onClick={() => canBoth ? setViewport("both") : openUpgradeInNewTab()}
                >
                  <BothIcon /> Both {!canBoth && <span className="rc-pro">Pro</span>}
                </button>
              </div>
            </div>

            {/* Pages — one control replaces the old scope + pages
                pair. "Just this page" is the 1-page case stated in
                words, so it reads as intent ("only the URL I entered")
                rather than a bare ceiling; higher counts crawl from
                that URL. The hint carries that mental model. Always
                shown now — there's no separate scope toggle to gate it,
                and the backend only ever received the page count. */}
            <div className="rc-settings-row">
              <SettingLabel icon={<PagesGlyph />}>Pages to capture</SettingLabel>
              <div className="rc-pages-control">
                <div className="rc-toggle rc-toggle-pages">
                  {PAGE_OPTIONS.map((n) => {
                    const locked = n > maxPages;
                    const isSingle = n === 1;
                    return (
                      <button
                        key={n}
                        type="button"
                        className={`rc-toggle-btn${isSingle ? "" : " rc-toggle-num"}${pages === n ? " active" : ""}${locked ? " rc-locked" : ""}`}
                        aria-pressed={pages === n}
                        aria-disabled={locked}
                        aria-label={isSingle ? "Just this page" : `Up to ${n} pages`}
                        title={locked ? `Auditing ${n} pages per run is on the Pro plan. Click to see pricing.` : undefined}
                        onClick={() => locked ? openUpgradeInNewTab() : setPages(n)}
                      >
                        {isSingle ? "Just this page" : n}
                        {locked && <span className="rc-pro">Pro</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="rc-pages-hint">Higher counts crawl from your URL and capture more pages.</p>
              </div>
            </div>

            {/* AI assistance — available for audits AND screenshots (it
                directs which pages or which state to capture), hidden only
                for image uploads where there's no live page to drive. Same
                .rc-accordion pattern as the dashboard so the two surfaces
                feel like one product. */}
            {source === "url" && (
              <div className={`rc-accordion${aiOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className={`rc-accordion-head${!canAi ? " is-locked" : ""}`}
                  aria-expanded={canAi ? aiOpen : false}
                  aria-controls="nr-ai-instructions"
                  aria-disabled={!canAi}
                  onClick={() => canAi ? setAiOpen((v) => !v) : openUpgradeInNewTab()}
                >
                  <span className="rc-accordion-chev" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </span>
                  <span className="rc-accordion-title">AI assistance</span>
                  <span className="rc-accordion-meta">optional · +5 credits</span>
                  {!canAi && <span className="rc-pro">Pro</span>}
                </button>
                {aiOpen && canAi && (
                  <div className="rc-accordion-body">
                    <p className="rc-ai-hint">
                      Tell the AI which pages to capture (e.g. &ldquo;pricing, checkout, FAQ&rdquo;) or which state to reach first (e.g. &ldquo;open the menu, switch to dark mode&rdquo;). Works for audits and screenshots.
                    </p>
                    <textarea
                      id="nr-ai-instructions"
                      className="rc-ai-textarea"
                      aria-label="AI assistance instructions"
                      rows={2}
                      maxLength={100}
                      spellCheck
                      placeholder="Example: capture pricing & checkout — or open the mobile menu first."
                      value={aiNote}
                      onChange={(e) => setAiNote(e.target.value.slice(0, 100))}
                    />
                    <div className="rc-ai-row">
                      <div className="rc-ai-pills">
                        {AI_EXAMPLES.map((ex) => (
                          <button key={ex} type="button" className="rc-ai-pill" onClick={() => setAiNote(ex)}>{ex}</button>
                        ))}
                      </div>
                      <span className="rc-ai-count">{aiNote.length}/100</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {error && <p className="rc-bar-error" role="alert">{error}</p>}

        {/* Short-by warning sits above the CTA so the user sees the
            shortfall before they click. Add credits / upgrade are
            inline so they can act without leaving the surface. */}
        {!enoughCredits && (
          <p className="rc-runline-warn" role="status">
            Short by {creditCost - credits} credit{creditCost - credits !== 1 ? "s" : ""}.{" "}
            <Link href="/account#topup">Add credits</Link>
            {" or "}
            <AppToMarketingLink href="/pricing#plans">upgrade</AppToMarketingLink>.
          </p>
        )}

        {/* CTA row — cost lives on the button label so users see what
            they're committing to right at the click moment. Matches
            the dashboard creator's pattern. */}
        <div className="rc-cta-row">
          <button
            type="submit"
            className="rc-bar-btn"
            disabled={submitting || checkingUrl}
          >
            {ctaLabel}
          </button>
        </div>
      </form>
        </div>
        {/* Aside content — was previously empty for "form is the
            job, don't add noise". But a blank panel reads as broken
            to first-time users, and the page-margin space is real
            estate that can answer the natural "what am I about to
            get?" question. Kept quiet: a single small card with
            outcome bullets, no CTAs, no marketing. */}
        <aside className="app-aside" aria-label="What to expect">
          <section className="nr-expect-card">
            <h3 className="nr-expect-title">What you&rsquo;ll get back</h3>
            <ul className="nr-expect-list">
              <li>Captured pages with AI-pinned findings on the screenshot.</li>
              <li>Usability heuristics + WCAG accessibility checks.</li>
              <li>Severity-colored badges (high · medium · low) with notes.</li>
              <li>Shareable read-only link &middot; PDF export.</li>
            </ul>
            <p className="nr-expect-time">
              Typical audit: about <strong>1 minute per page</strong>. Your audit
              shows up in <strong>Files</strong> when it&rsquo;s ready.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exported wrapper (Suspense for useSearchParams)                     */
/* ------------------------------------------------------------------ */

export function AuditConfigClient() {
  return (
    <Suspense fallback={<AuditConfigSkeleton />}>
      <AuditConfigInner />
    </Suspense>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline SVG icons                                                    */
/* ------------------------------------------------------------------ */

function LinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function DesktopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
}

function BothIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="5" width="12" height="8.5" rx="1.8" />
      <path d="M8.5 13.5v3" />
      <path d="M6.5 16.5h4" />
      <rect x="16" y="3.5" width="5.5" height="14.5" rx="1.8" />
      <path d="M18.75 15.5h.01" />
    </svg>
  );
}

function UploadCloudIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
